import {
  checkUsernameExists,
  decryptPasswordWithRecoveryKey,
} from "@/middleware/forgotPassword";
import { t, TFunction } from "i18next";
import { z } from "zod";
import { MAX_PASSWORD_LENGTH } from "@/config/constants";

export const isValidPassword = (passwordJSON: string): boolean => {
  try {
    const obj = JSON.parse(passwordJSON);
    return obj.password && obj.password !== "";
  } catch {
    return false;
  }
};
export const getUsernameFormSchema = (tf: TFunction) =>
  z
    .object({
      username: z
        .string()
        .min(3, {
          // TO DO => Check what sort of characters are allowed (apply small only)
          message: tf("usernameToShort"),
        })
        .max(30, {
          message: tf("usernameToLong"),
        })
        .regex(/^[a-z0-9._-]+$/, {
          message: tf("usernameRestriction"),
        }),
    })
    .superRefine(async (data, ctx) => {
      try {
        const userExists = await checkUsernameExists(data.username);
        return userExists;
      } catch (error) {
        let message = tf("errorOccurred");

        if (error instanceof Error && error.message) {
          try {
            const msgObject = JSON.parse(error.message);
            message = tf(msgObject.title);
          } catch {
            message = error.message;
          }
        }

        ctx.addIssue({
          path: ["username"],
          code: z.ZodIssueCode.custom,
          message: message,
        });
      }
    });

// UsernameFormType -> Infer the type from the Zod Schema -> Username Form
export type UsernameFormType = z.infer<
  ReturnType<typeof getUsernameFormSchema>
>;

export const validateWithUsernameAndPassword = async (
  entered: string[],
  ctx: z.RefinementCtx,
  expected: {
    username: string;
    encryptedPassword: string;
  },
) => {
  const recoveryKey = entered.map((word) => word.toLowerCase()).join(" ");

  try {
    const passwordJSON = await decryptPasswordWithRecoveryKey(
      expected.username,
      recoveryKey,
      expected.encryptedPassword,
    );
    if (!isValidPassword(passwordJSON)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("recover.confirmPhraseError"),
        path: [],
      });
    }
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("recover.confirmPhraseError"),
      path: [],
    });
  }
};

export const PasswordSchema = z.object({
  password: z
    .string()
    .min(6, {
      message: "Password must be at least 6 characters.",
    })
    .max(MAX_PASSWORD_LENGTH, {
      message: `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`,
    }),
});
export type PasswordType = z.infer<typeof PasswordSchema>;
